import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { X, Upload, Image as ImageIcon, Trash2, Loader2, Plus, ZoomIn, Pencil, Check } from 'lucide-react';
import { getGarmentImages, addGarmentImage, deleteGarmentImage, uploadGarmentImage, updateGarmentImage } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NO_IMAGE_PLACEHOLDER =
  'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%2394a3b8" font-size="12">No Image</text></svg>';

// Single shared "Reference Images" modal used by BOTH the Garments page and the
// EditOrderPage Garments tab, so upload/paste/description/delete behavior stays
// identical and only needs to be fixed in one place going forward.
const GarmentImagesModal = ({ garment, isOpen, onClose, onUpdate }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [editingDescriptionId, setEditingDescriptionId] = useState(null);
  const [descriptionText, setDescriptionText] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageName, setImageName] = useState('');
  const fileInputRef = useRef(null);

  const fetchImages = useCallback(async () => {
    if (!garment?.garment_id) return;
    setLoading(true);
    try {
      const response = await getGarmentImages(garment.garment_id);
      setImages(response.data || []);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [garment?.garment_id]);

  useEffect(() => {
    if (isOpen && garment?.garment_id) {
      fetchImages();
    }
  }, [isOpen, garment?.garment_id, fetchImages]);

  // Compress/resize an image client-side before upload (max 1200x1200, 80% quality)
  const compressImage = (file, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => resolve(file);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(file);
      reader.readAsDataURL(file);
    });
  };

  const uploadFiles = useCallback(async (files) => {
    const imageFiles = (files || []).filter((file) => file?.type?.startsWith('image/'));
    if (imageFiles.length === 0) {
      toast.error('Please provide image files only');
      return;
    }

    setUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of imageFiles) {
      try {
        const compressedFile = await compressImage(file, 1200, 1200, 0.8);
        if (compressedFile.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is still too large after compression`);
          errorCount += 1;
          continue;
        }
        await uploadGarmentImage(garment.garment_id, compressedFile, 'reference');
        successCount += 1;
      } catch (error) {
        console.error('Error uploading image:', error);
        errorCount += 1;
      }
    }

    if (successCount > 0) {
      toast.success(`Uploaded ${successCount} image(s)`);
      fetchImages();
      onUpdate?.();
    }
    if (errorCount > 0) {
      toast.error(`Failed to upload ${errorCount} image(s)`);
    }

    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [garment?.garment_id, onUpdate, fetchImages]);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    await uploadFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []).filter((file) => file.type?.startsWith('image/'));
    await uploadFiles(files);
  };

  // Clipboard paste support (Ctrl/Cmd + V) while the modal is open.
  useEffect(() => {
    if (!isOpen || !garment?.garment_id) return;

    const handlePaste = async (event) => {
      if (uploading) return;
      const items = Array.from(event.clipboardData?.items || []);
      const imageItems = items.filter((item) => item.type?.startsWith('image/'));
      if (imageItems.length === 0) return;

      event.preventDefault();
      const files = imageItems
        .map((item, index) => {
          const blob = item.getAsFile();
          if (!blob) return null;
          const ext = blob.type?.split('/')[1] || 'png';
          return new File([blob], `pasted-image-${Date.now()}-${index}.${ext}`, {
            type: blob.type || 'image/png',
            lastModified: Date.now(),
          });
        })
        .filter(Boolean);

      if (files.length > 0) {
        await uploadFiles(files);
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isOpen, garment?.garment_id, uploading, uploadFiles]);

  const handleAddImageFromUrl = async () => {
    if (!imageUrl.trim()) {
      toast.error('Please enter an image URL');
      return;
    }

    setUploading(true);
    try {
      await addGarmentImage(garment.garment_id, {
        garment_id: garment.garment_id,
        image_url: imageUrl,
        image_name: imageName || 'Reference Image',
        image_type: 'reference',
      });
      toast.success('Image added successfully');
      setImageUrl('');
      setImageName('');
      fetchImages();
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to add image');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      await deleteGarmentImage(garment.garment_id, imageId);
      toast.success('Image deleted');
      fetchImages();
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Failed to delete image');
    }
  };

  const handleStartEditDescription = (image) => {
    setEditingDescriptionId(image.image_id);
    setDescriptionText(image.description || '');
  };

  const handleCancelEditDescription = () => {
    setEditingDescriptionId(null);
    setDescriptionText('');
  };

  const handleSaveDescription = async (imageId) => {
    try {
      await updateGarmentImage(garment.garment_id, imageId, { description: descriptionText });
      toast.success('Description updated');
      setEditingDescriptionId(null);
      setDescriptionText('');
      fetchImages();
    } catch (error) {
      toast.error('Failed to update description');
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
    // Use /api/uploads path for proper routing through Kubernetes ingress
    const apiPath = imagePath.startsWith('/uploads') ? `/api${imagePath}` : imagePath;
    return `${API_URL}${apiPath}`;
  };

  const garmentDisplayNo = garment?.garment_no?.slice(-8)?.toUpperCase() || 'Garment';

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="garment-images-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Reference Images
              <Badge variant="outline" className="ml-2 font-mono">
                {garmentDisplayNo}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              Upload reference images for this garment. These will appear in the Order Sheet PDF.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Upload Area */}
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                uploading ? 'border-blue-400 bg-blue-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
              }`}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              data-testid="garment-images-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="garment-images-file-input"
              />

              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  <p className="text-sm text-slate-600">Uploading images...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload className="w-8 h-8 text-slate-400" />
                  <p className="text-sm text-slate-600">Drag & drop images here, or</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="garment-images-select-files-button"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Select Files
                  </Button>
                  <p className="text-xs text-slate-400 mt-1">
                    Supports JPG, PNG, WebP. Max 10MB per file. Multiple files allowed.
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-testid="garment-images-paste-hint">
                    Tip: You can paste a screenshot here (Ctrl/Cmd + V).
                  </p>
                </div>
              )}
            </div>

            {/* URL Input Section (collapsed by default) */}
            <details className="border rounded-lg">
              <summary className="px-4 py-2 cursor-pointer text-sm text-slate-600 hover:bg-slate-50">
                Or add image via URL
              </summary>
              <div className="p-4 pt-2 space-y-2 border-t">
                <Input
                  placeholder="Enter image URL..."
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  className="text-sm"
                  data-testid="garment-images-url-input"
                />
                <Input
                  placeholder="Image name (optional)"
                  value={imageName}
                  onChange={(e) => setImageName(e.target.value)}
                  className="text-sm"
                  data-testid="garment-images-url-name-input"
                />
                <Button
                  onClick={handleAddImageFromUrl}
                  disabled={uploading || !imageUrl}
                  size="sm"
                  className="w-full"
                  data-testid="garment-images-add-url-button"
                >
                  Add from URL
                </Button>
              </div>
            </details>

            {/* Images Grid */}
            <div>
              <Label className="text-sm font-medium mb-2 block">
                Uploaded Images ({images.length})
              </Label>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : images.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No images uploaded yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3">
                  {images.map((image, index) => (
                    <div
                      key={image.image_id}
                      className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-100"
                      data-testid={`garment-image-card-${image.image_id}`}
                    >
                      <div className="aspect-square">
                        <img
                          src={getImageUrl(image.thumbnail_url || image.image_url)}
                          alt={image.image_name || `Image ${index + 1}`}
                          className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => setSelectedImage(image)}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = NO_IMAGE_PLACEHOLDER;
                          }}
                        />

                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="flex gap-2">
                            <Button
                              size="icon"
                              variant="secondary"
                              className="h-8 w-8"
                              onClick={() => setSelectedImage(image)}
                              data-testid={`garment-image-zoom-button-${image.image_id}`}
                            >
                              <ZoomIn className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="secondary"
                              className="h-8 w-8"
                              onClick={() => handleStartEditDescription(image)}
                              data-testid={`garment-image-edit-description-button-${image.image_id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="destructive"
                              className="h-8 w-8"
                              onClick={() => handleDeleteImage(image.image_id)}
                              data-testid={`garment-image-delete-button-${image.image_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        <Badge variant="secondary" className="absolute top-1 left-1 text-[10px] px-1.5 py-0">
                          {index + 1}
                        </Badge>
                      </div>

                      {/* Description */}
                      <div className="p-2 border-t bg-white">
                        {editingDescriptionId === image.image_id ? (
                          <div className="flex gap-1">
                            <Input
                              value={descriptionText}
                              onChange={(e) => setDescriptionText(e.target.value)}
                              placeholder="Add description..."
                              className="h-7 text-xs"
                              autoFocus
                              data-testid={`garment-image-description-input-${image.image_id}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSaveDescription(image.image_id);
                                if (e.key === 'Escape') handleCancelEditDescription();
                              }}
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 shrink-0"
                              onClick={() => handleSaveDescription(image.image_id)}
                              data-testid={`garment-image-save-description-button-${image.image_id}`}
                            >
                              <Check className="w-3 h-3" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 shrink-0"
                              onClick={handleCancelEditDescription}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <p
                            className="text-xs text-slate-600 truncate cursor-pointer hover:text-slate-900"
                            onClick={() => handleStartEditDescription(image)}
                            title={image.description || 'Click to add description'}
                          >
                            {image.description || <span className="italic text-slate-400">Add description...</span>}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-4 border-t">
            <p className="text-sm text-slate-500">
              {images.length} image(s) &bull; These images will appear in the Order Sheet PDF
            </p>
            <Button variant="outline" onClick={onClose} data-testid="garment-images-modal-close-button">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Lightbox */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden" data-testid="garment-image-lightbox">
          <div className="relative">
            <Button
              size="icon"
              variant="ghost"
              className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/70 text-white"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-4 h-4" />
            </Button>
            {selectedImage && (
              <img
                src={getImageUrl(selectedImage.image_url)}
                alt={selectedImage.image_name || 'Preview'}
                className="w-full h-auto max-h-[85vh] object-contain bg-black"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = NO_IMAGE_PLACEHOLDER;
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GarmentImagesModal;
